import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Field } from "@/components/Field";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const PasswordForm = ({ onDone, forced }) => {
  const { setUser } = useApp();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setOk("");
    if (f.new_password.length < 8) { setErrors({ new_password: "Password must be at least 8 characters" }); return; }
    if (f.new_password !== f.confirm) { setErrors({ confirm: "Passwords do not match" }); return; }
    setBusy(true); setErrors({});
    try {
      const { data } = await api.post("/auth/change-password", f);
      setOk(data.message);
      toast.success(data.message);
      setUser(data.user);
      setF({ current_password: "", new_password: "", confirm: "" });
      onDone?.();
    } catch (err) {
      const { message, fields } = apiError(err);
      setErrors(Object.keys(fields).length ? fields : { form: message });
    } finally {
      setBusy(false);
    }
  };
  const inp = (k, label) => (
    <Field label={label} name={k} error={errors[k]}>
      <Input id={k} type="password" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
        className={`h-11 bg-card ${errors[k] ? "border-destructive" : ""}`} data-testid={`pw-${k.replace(/_/g, "-")}-input`} />
    </Field>
  );
  return (
    <form onSubmit={submit} noValidate className="space-y-5" data-testid="change-password-form">
      {inp("current_password", forced ? "Temporary password" : "Current password")}
      {inp("new_password", "New password")}
      {inp("confirm", "Confirm new password")}
      {errors.form && <p className="text-sm text-destructive" data-testid="pw-form-error">{errors.form}</p>}
      {ok && <p className="text-sm font-medium text-emerald-700" data-testid="pw-success">{ok}</p>}
      <Button type="submit" disabled={busy} className="h-11 w-full" data-testid="pw-submit-button">{busy && <Loader2 className="animate-spin" />} Update password</Button>
    </form>
  );
};

export default function ChangePassword({ forced }) {
  const navigate = useNavigate();
  if (forced) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 py-12" data-testid="forced-password-page">
        <div className="fade-up w-full max-w-sm">
          <Logo mark className="w-16" />
          <div className="eyebrow mt-6">Security</div>
          <h1 className="mt-2 text-3xl font-semibold">Set a new password</h1>
          <p className="mb-6 mt-2 text-sm text-muted-foreground">You signed in with a temporary password. Choose a new one to continue to your portal.</p>
          <PasswordForm forced onDone={() => navigate("/", { replace: true })} />
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-md" data-testid="change-password-page">
      <h1 className="flex items-center gap-3 text-3xl font-semibold tracking-tight"><KeyRound className="h-6 w-6" /> Change password</h1>
      <div className="gold-rule mb-8 mt-4 w-24" />
      <div className="rounded-lg border bg-card p-6"><PasswordForm /></div>
    </div>
  );
}
